import { Badge, Button, Checkbox, Col, Collapse, Descriptions, Input, Modal, Popover, Row, Select, Space, Tag } from "antd";
import moment from 'moment';
import { useEffect, useState } from "react";
import CardBox from "../../../../components/cardBox";
import CopyableContent from "../../../../components/copyableContent";
import CustomCollapse from "../../../../components/customCollapse";
import CustomCollapseHeader from "../../../../components/customCollapseHeader";
import { renderErrorMessage } from "../../../../components/messages/errorMessage";
import TaxCaseStore from "../../../../contexts/taxCaseStore";
import _t from "../../../../lang/translate";
import format from "../../../../utilities/formatNumbers";
import { exclusionReasons } from "../taxFlow/util";
import AdvertEquipment from "./details/advertEquipment";
import AdvertImageGallery from "./details/advertImageGallery";
import AdvertRegulations from "./details/advertRegulation";
import { overrideAdvertVin, updateAdvertAugmentation } from "./service";
import { AdvertEquipmentSource, TaxAdvert, TaxAdvertConfig } from "./types";
import Icon from "../../../../assets/icons/icon";
import { isValidVin } from "../../../../utilities/stringExtraFunctions";
import openModal from "../../../../components/modal/openModal";

const reasons = [
  { value: 1, label: "Indgår" },
  ...(exclusionReasons
    ?.toSorted(
      (a, b) => a?.label?.toString()?.localeCompare(b?.label?.toString() ?? "") ?? 0
    ) ?? [])
]
type Props = {
  advert: TaxAdvert;
  visible: boolean;
  enrichmentQuality: "LOW" | "HIGH"
  setVisible: (v: boolean) => void;
  submit: (advert: TaxAdvert) => void;
}

export type EnrichmentData = Pick<TaxAdvertConfig, "enrichment" | "enrichmentMeta">
export default function AdvertDetails({ advert, enrichmentQuality, visible, setVisible, submit }: Props) {
  const { taxCase } = TaxCaseStore.useContainer()
  const [reasonId, setReasonId] = useState(reasons.find(r => r.label?.toString() === advert.config?.exclusionReason)?.value ?? 1)
  const [regulations, setRegulations] = useState(advert.config?.regulations)
  const [note, setNote] = useState(advert.config?.note)
  const [newPriceManager, setNewPriceManager] = useState(advert.config?.newPriceManager)
  const [equipmentSource, setEquipmentSource] = useState<AdvertEquipmentSource>(advert.config?.equipmentSource ?? "taxCase")
  const [advertEcode, setAdvertEcode] = useState(advert.eCode)
  const [renderDescription, setRenderDescription] = useState(advert.description ?? "")
  const [vinOverride, setVinOverride] = useState(advert.vinOverride)
  const [enrichmentData, setEnrichmentData] = useState<EnrichmentData>(() => ({
    enrichment: advert.config?.enrichment,
    enrichmentMeta: advert.config?.enrichmentMeta,
  }))
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [showAllMatchedTerms, setShowAllMatchedTerms] = useState(false)

  const [overridingVin, setOverridingVin] = useState(false)


  useEffect(() => {
    if (visible && advert) {
      setReasonId(reasons.find(r => r.label?.toString() === advert.config?.exclusionReason)?.value ?? 1)
      setRegulations(advert.config?.regulations)
      setNote(advert.config?.note)
      setNewPriceManager(advert.config?.newPriceManager)
      setEquipmentSource(advert.config?.equipmentSource ?? "taxCase")
      setAdvertEcode(advert.eCode)
      setEnrichmentData({
        enrichment: advert.config?.enrichment,
        enrichmentMeta: advert.config?.enrichmentMeta,
      })
      setRenderDescription(advert.description ?? "")
      setSelectedTags(new Set())
      setVinOverride(advert.vinOverride)
    }
  }, [visible, advert])

  function handleCancel(e: any) {
    const isMaskClick = e?.currentTarget?.classList?.contains("ant-modal-wrap");

    if (isMaskClick) {
      confirmClose();
    } else {
      setVisible(false);
    }
  }
  function confirmClose() {
    Modal.confirm({
      title: "Gem ændringer?",
      content: "Vil du gerne gemme dine ændringer?",
      okText: _t("save", "close", " & "),
      cancelText: "Fortsæt",
      onOk: handleSubmit,
      autoFocusButton: "ok"
    });
  }
  async function handleSubmit() {
    const exclusionReason = reasons.find(r => r.value === reasonId)?.label?.toString()
    submit({
      ...advert,
      eCode: advertEcode,
      vinOverride,
      config: {
        ...advert.config,
        isIncluded: reasonId === 1,
        exclusionReason,
        note,
        regulations,
        newPriceManager,
        equipmentSource,
        ...enrichmentData
      },
    })
    setVisible(false)
    if (advert.config?.isCustom) return;
    try {
      const mountedEquipmentCodes = newPriceManager?.data?.equipment
        .filter(eq => eq.mounted)
        .map(eq => eq.code) as string[]
      const manualEquipment = newPriceManager?.data.equipment.filter(eq => eq.isCustom)
      const eCode = advert.config?.equipmentSource === 'taxCase'
        ? advert.config?.enrichment?.eCodeContext ?? taxCase.vehicle?.motorAtlasId
        : advert.eCode;
      await updateAdvertAugmentation({
        advertId: String(advert.id),
        equipment: mountedEquipmentCodes,
        motorAtlasId: eCode ?? "",
        manualEquipment: manualEquipment?.map(eq => {
          return {
            code: eq.code,
            id: null,
            author: null,
            currencyCode: null,
            equipmentCategory: null,
            name: eq.name,
            price: eq.amount,
            priceIsKnown: true,
            prices: [{ amount: eq.amount ?? 0, currency: "DKK", isWithTax: eq.isWithTax }],
            savedAt: null,
            source: eq.source,
            sourceId: eq.sourceId,
            subEquipments: [],
            typeCategory: eq.category,
          }
        })
      }) // Status code 204
    } catch (error) {
      renderErrorMessage(error)
    }
  }

  const handleTagsChange = (tag: string, checked: boolean) => {
    setSelectedTags((prev) => {
      const next = new Set(prev)
      if (checked) next.add(tag)
      else next.delete(tag)
      return next
    });
    const newMarkup = highlightDescription(renderDescription, tag, checked)
    setRenderDescription(newMarkup)
  };
  function highlightDescription(prevMarkup: string, tag: string, checked: boolean) {
    let markup = prevMarkup ?? ""
    const index = markup.indexOf(tag)
    const len = tag.length
    if (checked) { // Add mark
      markup = markup.slice(0, index) + '<mark>' + markup.slice(index, index + len) + '</mark>' + markup.slice(index + len)
    } else { // Remove mark
      markup = markup.slice(0, index - '<mark>'.length) + markup.slice(index, index + len) + markup.slice(index + len + '</mark>'.length)
    }
    return markup
  }

  function toggleAllMatchedTerms(checked: boolean) {
    const terms = Object.keys(enrichmentData.enrichment?.matchedTerms ?? {})
    setShowAllMatchedTerms(checked)
    setSelectedTags(checked ? new Set(terms) : new Set());
    let markup = advert.description ?? "";
    if (checked) {
      for (const tag of terms) {
        markup = highlightDescription(markup, tag, checked)
      }
    }
    setRenderDescription(markup)
  }

  async function overrideVin() {
    try {
      setOverridingVin(true)
      const { vinOverride: vin } = await openModal('overrideAdvertVin', { vinOverride: vinOverride }) as { vinOverride: string }
      await overrideAdvertVin({ marketplaceEntry: advert.id, vin: vin })
      setVinOverride(vin)
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setOverridingVin(false)
    }
  }

  return (
    <Modal visible={visible} width={1400} onCancel={handleCancel} onOk={handleSubmit}
      destroyOnClose
      closable={false}
      className="advert-detail-modal"
      title={
        <Row justify="space-between">
          <Col>
            <Space align="center" size={"large"}>
              <span>{advert.manufacturer} {advert.name}</span>
              <Select showSearch optionFilterProp="label" allowClear placeholder={_t("choose_reason")}
                style={{ width: 200 }}
                value={reasonId} onChange={setReasonId}
                options={reasons} />
            </Space>
          </Col>
          <Col>
            <Button onClick={handleSubmit}>{_t("save", "close", " & ")}</Button>
          </Col>
        </Row>
      }>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <div className="p-1 border-light border-radius">
            <Row justify="space-between" align="middle" className="mb-1">
              <Col>
                <h4 className="mb-0">{_t("data_from_bilinfo")}</h4>
              </Col>
              <Col><small>{_t("seller")}: {advert.seller?.name}, {advert.seller?.zip} {advert.seller?.city}</small></Col>
            </Row>
            <Descriptions className="advert-details-description" layout="vertical" size="small" column={4} colon={false}>
              <Descriptions.Item label={_t("firstRegDate")}>{format.date(advert.firstRegDate)}</Descriptions.Item>
              <Descriptions.Item label={_t("mileage")}>{format.milage(advert.mileage)}</Descriptions.Item>
              <Descriptions.Item style={{ width: 200 }} label={_t("vin")}>
                <Space>
                  <span>{advert.vin ?? vinOverride}</span>
                  {!isValidVin(advert.vin) && advert.config?.isCustom && (
                    <Popover title={"Manuel annonce: Handling ikke tilgængelig"} content={"Man kan ikke ændre stelnummer på manuelle annoncer."}>
                      <Button size="small" type="text" icon={<Icon name="pencil-simple" size="large" />} />
                    </Popover>
                  )}
                  {!isValidVin(advert.vin) && !advert.config?.isCustom && (
                    <Button onClick={overrideVin} size="small" type="text" loading={overridingVin} icon={<Icon name="pencil-simple" size="large" />} />
                  )}
                </Space>
              </Descriptions.Item>


              <Descriptions.Item label={_t("type")}>{advert.type}</Descriptions.Item>

              <Descriptions.Item label={_t("fuel")}>{advert.fuel}</Descriptions.Item>
              <Descriptions.Item label={_t("horse_power_full")}>{advert.horsePower}</Descriptions.Item>
              <Descriptions.Item label={_t("model_period")}>{advert.modelYear}</Descriptions.Item>
              <Descriptions.Item label={_t("storageDays")}>{moment().diff(moment(advert.validFrom), 'days')}</Descriptions.Item>
            </Descriptions>

            <div className="border-light border-radius mt-2 pb-1">
              <div className="p-1">
                <h4> {_t("imageGallery")} </h4>
              </div>
              <AdvertImageGallery advert={advert} />
            </div>
            {!enrichmentData.enrichment?.isInferred && (
              <CustomCollapse defaultActiveKey={'regualtions'} className="ml-0 mr-0 mt-1 mb-0">
                <Collapse.Panel key={"description"} header={
                  <CustomCollapseHeader>
                    {_t("advertDescription")}
                  </CustomCollapseHeader>
                }>
                  <CardBox style={{ whiteSpace: "pre-line", wordBreak: "break-word" }}>
                    <div dangerouslySetInnerHTML={{ __html: renderDescription }}></div>
                  </CardBox>
                </Collapse.Panel>
              </CustomCollapse>
            )}
          </div>


        </Col>
        <Col span={12}>
          <div className="p-1 border-light border-radius">
            <h4>{_t("regulations")}</h4>
            <AdvertRegulations advert={advert} regulations={regulations} setRegulations={setRegulations} />

            <CustomCollapse className="ml-0 mr-0 mb-0 mt-2">
              <Collapse.Panel key={"1"} header={
                <CustomCollapseHeader>
                  <Badge status={!!note ? "success" : "warning"} dot />
                  {_t("comment_to_advert")}
                </CustomCollapseHeader>
              }>
                <Input.TextArea rows={5} value={note ?? undefined} onChange={(e) => setNote(e.target.value)} />
              </Collapse.Panel>
            </CustomCollapse>
          </div>

        </Col>
        {enrichmentData.enrichment?.isInferred && (
          <Col span={24}>
            <CustomCollapse defaultActiveKey={'regualtions'} className="ml-0 mr-0 mt-0 mb-0">
              <Collapse.Panel key={"description"} header={
                <CustomCollapseHeader>
                  {_t("advertDescription")}
                </CustomCollapseHeader>
              }>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <CardBox style={{ whiteSpace: "pre-line", wordBreak: "break-word" }}>
                      <div dangerouslySetInnerHTML={{ __html: renderDescription }}></div>
                    </CardBox>
                  </Col>
                  <Col span={12}>
                    <div className="">
                      <Row justify="space-between">
                        <Col>
                          <h3>AI Matches</h3>
                        </Col>
                        <Col>
                          <Checkbox onChange={(e) => toggleAllMatchedTerms(e.target.checked)} value={showAllMatchedTerms}>Vis alle</Checkbox>
                        </Col>
                      </Row>
                      <Row gutter={[8, 8]}>
                        {Object.keys(enrichmentData.enrichment?.matchedTerms ?? {}).map((tag, index) => {
                          return (
                            <Tag.CheckableTag checked={selectedTags.has(tag)}
                              onChange={(checked) => handleTagsChange(tag, checked)}
                              key={tag + "_" + index} className="inferred-equipment-tag ant-tag-exception"
                            >{tag}</Tag.CheckableTag>
                          )
                        })}
                      </Row>
                    </div>

                  </Col>
                </Row>
              </Collapse.Panel>

            </CustomCollapse>
          </Col>
        )}
      </Row>


      <CustomCollapse defaultActiveKey={["1"]} className="ml-0 mr-0 mb-0 mt-2">
        <Collapse.Panel key={"1"} header={
          <CustomCollapseHeader>
            <Row justify="space-between" align="middle">
              <Col>
                {_t("equipmentCatalog")}{' '}
                <span className="text-tiny text-gray">- Konfigureret fra kilde: <strong>Vurderingskøretøjet</strong></span>
              </Col>
              <Col onClick={e => e.stopPropagation()}>
                <CopyableContent contentToCopy={newPriceManager?.values.equipmentPrice?.toString() ?? ""}>
                  {_t("inTotal")} {format.pricePrefixed(newPriceManager?.values.equipmentPrice)}
                </CopyableContent>
              </Col>
            </Row>
          </CustomCollapseHeader>
        }>
          <AdvertEquipment advert={advert} equipmentSource={equipmentSource} setEquipmentSource={setEquipmentSource}
            newPriceManager={newPriceManager} setNewPriceManager={setNewPriceManager}
            advertEcode={advertEcode} setAdvertEcode={setAdvertEcode}
            enrichmentData={enrichmentData} setEnrichmentData={setEnrichmentData}
            enrichmentQuality={enrichmentQuality}
            vinOverride={vinOverride}
          // handleTagsChange={handleTagsChange}
          // selectedTags={selectedTags}
          />
        </Collapse.Panel>
      </CustomCollapse>
    </Modal>
  )
}
